import {useContext, useState} from "react";
import {API_SHARE_MARKETPLACE_OFFERS, API_SHARE_MARKETPLACE_MY_REQUESTS, API_SHARE_MARKETPLACE_ADMIN_OFFERS} from "../common/api/apiConstants";
import {shareMarketplaceAPI} from "../common/api/shareMarketplaceAPI";
import {createURLQueryFromParams} from "../common/api/apiUtil";
import {useKeycloakFunctions} from "./useKeycloakFunctions";
import {OFFER_CREATED_AT, OFFER_EXPIRES_AT} from "../common/constant";
import {LocaleContext} from "../store/providers/LocaleProvider";

export const useMarketplaceOffers = () => {

    const {initialized, authenticated, isAdmin} = useKeycloakFunctions();
    const {localeUppercaseUnderscore} = useContext(LocaleContext)[0];

    const [buyerTotalOffers, setBuyerTotalOffers] = useState(0);
    const [buyerOffers, setBuyerOffers] = useState([]);
    const [loadingBuyerOffers, setLoadingBuyerOffers] = useState(false);
    const [errorBuyerOffers, setErrorBuyerOffers] = useState(null);

    const [totalOffersAvailable, setTotalOffersAvailable] = useState(null);
    const [offersAvailable, setOffersAvailable] = useState([]);
    const [loadingOffersAvailable, setLoadingOffersAvailable] = useState(false);
    const [errorOffersAvailable, setErrorOffersAvailable] = useState(null);

    const [totalOffersUnavailable, setTotalOffersUnavailable] = useState(null);
    const [offersUnavailable, setOffersUnavailable] = useState([]);
    const [loadingOffersUnavailable, setLoadingOffersUnavailable] = useState(false);
    const [errorOffersUnavailable, setErrorOffersUnavailable] = useState(null);

    const [sellerOffers, setSellerOffers] = useState([]);
    const [loadingSellerOffers, setLoadingSellerOffers] = useState(false);
    const [errorSellerOffers, setErrorSellerOffers] = useState(null);

    const [adminTotalOffers, setAdminTotalOffers] = useState(0);
    const [adminOffers, setAdminOffers] = useState([]);
    const [loadingAdminOffers, setLoadingAdminOffers] = useState(false);
    const [errorAdminOffers, setErrorAdminOffers] = useState(null);

    const getBuyerOffers = (locale, page, size, sort, sort_order, sellerCountries, minPricePerShare, maxPricePerShare, numberOfShares, excludePoints, skipContacted) => {

        if (initialized && authenticated) {
            setLoadingBuyerOffers(true);
            setErrorBuyerOffers(null);

            let params = {};
            params.mine = "false";
            params.locale = locale || "en-GB";
            params.page = page || 1;
            params.size = size || 10;
            params.skip_contacted = skipContacted !== undefined ? skipContacted : true;
            if (sort) {
                params.sort = sort;
            }
            if (sort_order) {
                params.sort_order = sort_order;
            }
            if (sellerCountries && sellerCountries.length > 0) {
                params.seller_country = sellerCountries;
            }
            if (minPricePerShare) {
                params.min_price_per_share = minPricePerShare;
            }
            if (maxPricePerShare) {
                params.max_price_per_share = maxPricePerShare;
            }

            if (numberOfShares) {
                params.number_of_shares = numberOfShares;
            }

            if (excludePoints) {
                params.exclude_points = excludePoints;
            }

            shareMarketplaceAPI.get(API_SHARE_MARKETPLACE_OFFERS + createURLQueryFromParams(params), {redirectOnError: true})
                .then(res => res.data)
                .then(response => {
                    setLoadingBuyerOffers(false);
                    if (response) {
                        setBuyerOffers(response.offers);
                        setBuyerTotalOffers(response.count);
                    }
                })
                .catch(err => {
                    setLoadingBuyerOffers(false);
                    setErrorBuyerOffers(err);
                });
        }

    };

    const getOffersAvailable = (sort, sort_order, page, size, locale) => {
        setLoadingOffersAvailable(true);
        setErrorOffersAvailable(null);
        if (initialized && authenticated) {
            let params = {};
            params.locale = locale || "en-GB";
            page ? params.page = page : null;
            size ? params.size = size : null;
            if (sort) {
                params.sort = sort;
            }
            if (sort_order) {
                params.sort_order = sort_order;
            }
            params.available = "true";

            shareMarketplaceAPI.get(API_SHARE_MARKETPLACE_MY_REQUESTS + createURLQueryFromParams(params), {redirectOnError: true})
                .then(res => res.data)
                .then(response => {
                    setLoadingOffersAvailable(false);
                    if (response) {
                        setOffersAvailable(response.requests);
                        setTotalOffersAvailable(response.count);
                    }
                })
                .catch(err => {
                    setLoadingOffersAvailable(false);
                    setErrorOffersAvailable(err);
                });

        }
    };

    const getOffersUnavailable = (sort, sort_order, page, size, locale) => {
        setLoadingOffersUnavailable(true);
        setErrorOffersUnavailable(null);
        if (initialized && authenticated) {
            let params = {};
            params.locale = locale || "en-GB";
            page ? params.page = page : null;
            size ? params.size = size : null;
            if (sort) {
                params.sort = sort;
            }
            if (sort_order) {
                params.sort_order = sort_order;
            }
            params.available = "false";

            shareMarketplaceAPI.get(API_SHARE_MARKETPLACE_MY_REQUESTS + createURLQueryFromParams(params), {redirectOnError: true})
                .then(res => res.data)
                .then(response => {
                    setLoadingOffersUnavailable(false);
                    if (response) {
                        setOffersUnavailable(response.requests);
                        setTotalOffersUnavailable(response.count);
                    }
                })
                .catch(err => {
                    setLoadingOffersUnavailable(false);
                    setErrorOffersUnavailable(err);
                });

        }
    };

    const getSellerOffers = (locale) => {

        if (initialized && authenticated) {
            setLoadingSellerOffers(true);
            setErrorSellerOffers(null);

            let params = {};
            params.mine = "true";
            params.locale = locale || "en-GB";
            params.page = 1;
            params.size = 100;
            params.sort = OFFER_EXPIRES_AT;
            params.sort_order = "asc";

            shareMarketplaceAPI.get(API_SHARE_MARKETPLACE_OFFERS + createURLQueryFromParams(params), {redirectOnError: true})
                .then(res => res.data)
                .then(response => {
                    setLoadingSellerOffers(false);
                    response && setSellerOffers(response.offers);
                })
                .catch(err => {
                    setLoadingSellerOffers(false);
                    setErrorSellerOffers(err);
                });
        }
    };

    const getAdminOffers = (locale, page, size, memberId, offerId) => {

        if (isAdmin()) {
            setLoadingAdminOffers(true);
            setErrorAdminOffers(null);

            let params = {};
            params.locale = localeUppercaseUnderscore || "en_GB";
            params.page = page || 1;
            params.size = size || 10;
            params.sort = OFFER_CREATED_AT;
            params.sort_order = "desc";

            if (memberId) {
                params.member_id = memberId;
            }
            if (offerId) {
                params.offer_id = offerId;
            }

            shareMarketplaceAPI.get(API_SHARE_MARKETPLACE_ADMIN_OFFERS + createURLQueryFromParams(params), {redirectOnError: true})
                .then(res => res.data)
                .then(response => {
                    setLoadingAdminOffers(false);
                    if (response) {
                        setAdminOffers(response.offers);
                        setAdminTotalOffers(response.count);
                    } else {
                        setAdminOffers([]);
                        setAdminTotalOffers(0);
                    }
                })
                .catch(err => {
                    setLoadingAdminOffers(false);
                    setErrorAdminOffers(err);
                });
        }

    };

    return {
        getBuyerOffers,
        buyerOffers,
        buyerTotalOffers,
        loadingBuyerOffers,
        errorBuyerOffers,

        getOffersAvailable,
        offersAvailable,
        totalOffersAvailable,
        loadingOffersAvailable,
        errorOffersAvailable,

        getOffersUnavailable,
        offersUnavailable,
        totalOffersUnavailable,
        loadingOffersUnavailable,
        errorOffersUnavailable,

        getSellerOffers,
        sellerOffers,
        loadingSellerOffers,
        errorSellerOffers,

        getAdminOffers,
        adminOffers,
        adminTotalOffers,
        loadingAdminOffers,
        errorAdminOffers
    };
};
